import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, of, tap, throwError } from 'rxjs';
import { Security } from '@capital-access/common/security';
import { ModuleName } from '@capital-access/common/utils';
import { ListItem } from '@capital-access/lists/common';
import {
  ActivityHistoryFilterRequest,
  CommonFooterModel,
  EXPORT_REPORT_INCLUDED_CONTACTS,
  EXPORT_REPORT_MODULE_NAME,
  ExportDataMap,
  ExportDataModel,
  FinancialHighlightsModel,
  IncludedContacts,
  ITINERARY_PREVIEW_ACTIVITY_LIMIT,
  ReportDataMap,
  ReportDataModel,
  ReportSection,
  ReportSectionConfigurationWithOrder,
  ReportType,
  ReportViewModelSection,
  SAMPLE_DATA_EVENT_ID,
  SAMPLE_DATA_PROFILE_ID,
  SAMPLE_DATA_SECURITY_ID,
  TemplateExportListItemWithComments
} from '../..';
import { DataSource } from '../models/data-source.enum';
import { EventBaseInfo, ReportExportError } from '../models/report-data.model';

@Injectable()
export class ReportDataService {
  constructor(private http: HttpClient) {}

  private cachedSampleReportData: Partial<Record<ReportType, ReportDataMap>> = {};
  private cachedSampleExportData: Partial<Record<ReportType, ExportDataMap[]>> = {};

  private baseUrl = '/api/reporting';

  getReportData(
    reportSections: ReportSectionConfigurationWithOrder[],
    security: {
      id: number;
      surveillance: boolean;
    } | null,
    culture: string,
    language: string,
    currency: string | null,
    timeZone: string,
    defaultPeerListId: number,
    relatedSecurities?: Security[] | null,
    selectedItem?: ListItem[] | [],
    isGoBackToProfileConfiguration: boolean = false,
    bnyReportTime: Date = new Date(),
    selectedItineraryTab: string = 'events',
    templateId?: number | null
  ) {
    if (!reportSections.length) {
      return of({} as ReportDataMap);
    }
    const securityId = security?.id;
    const dataSource = security?.surveillance ? DataSource.Surveillance : DataSource.Public;
    const reportType = reportSections[0].config.appliedReportType;
    const combinedSecurityIds = relatedSecurities?.map(security => security?.id);
    const exportReportModuleName = localStorage.getItem(EXPORT_REPORT_MODULE_NAME) as ModuleName;
    const exportReportIncludedContacts = localStorage.getItem(EXPORT_REPORT_INCLUDED_CONTACTS);
    const includedContacts = JSON.parse(exportReportIncludedContacts || '[]') as IncludedContacts[];
    const selectedListItem = selectedItem?.length ? (selectedItem[0] as ListItem) : undefined;
    const templateExportListItemWithComments = selectedListItem as TemplateExportListItemWithComments;
    const sections = reportSections
      .filter(
        item =>
          item.config.reportSection !== ReportSection.None &&
          item.isSelected &&
          reportType &&
          item.config.applicableReportTypes.includes(reportType)
      )
      .map(section => {
        if (
          exportReportModuleName === ModuleName.EngageAttendees &&
          reportType === ReportType.Institution &&
          section.config.name === 'contactBiography'
        ) {
          return ReportSection.ParticipantBiography;
        }

        return section.config.reportSection;
      });

    if (reportType === ReportType.Itinerary) {
      const itineraryEventId = templateExportListItemWithComments?.eventId;
      const itineraryActivityIds =
        (selectedItem as TemplateExportListItemWithComments[])
          ?.map(selectedItem => selectedItem?.activityId as string)
          ?.filter(id => id != null) ?? [];
      return this.loadItineraryReportData(
        isGoBackToProfileConfiguration ? [] : sections,
        ReportType.ItineraryTemplate,
        itineraryEventId!,
        itineraryActivityIds,
        securityId,
        dataSource,
        timeZone,
        currency,
        culture,
        language,
        defaultPeerListId,
        [] as ActivityHistoryFilterRequest[],
        templateExportListItemWithComments,
        selectedItem && selectedItem?.length > 0,
        selectedItineraryTab,
        isGoBackToProfileConfiguration ? null : templateId ?? null
      );
    }
    if (selectedListItem) {
      const profileId = `${selectedListItem?.prId ?? ''}|${selectedListItem?.crmId ?? ''}`;

      const activityHistoryFilters: ActivityHistoryFilterRequest[] =
        templateExportListItemWithComments?.filterActivities
          ? [
              {
                profileId: profileId,
                companyContactIds: templateExportListItemWithComments?.filterActivities?.companyContacts?.map(
                  contact => contact?.id as string
                ),
                externalInstitutionIds: templateExportListItemWithComments?.filterActivities?.externalParticipants
                  ?.filter(item => item?.type == 'institutions')
                  .map(institution => institution?.id as string),
                externalContactIds: templateExportListItemWithComments?.filterActivities?.externalParticipants
                  ?.filter(item => item?.type == 'contacts')
                  .map(institution => institution?.id as string)
              }
            ]
          : [];

      if (reportType === ReportType.BnyManagementReport) {
        if (!sections.includes(ReportSection.BnyHeader)) {
          sections.push(ReportSection.BnyHeader);
        }
        return this.loadBnyReportData(
          sections,
          reportType,
          false,
          securityId,
          timeZone,
          culture,
          language,
          securityId ? [securityId] : [],
          bnyReportTime
        );
      }

      return this.loadReportData(
        isGoBackToProfileConfiguration ? [] : sections,
        includedContacts,
        reportType,
        profileId,
        false,
        securityId,
        dataSource,
        timeZone,
        currency,
        culture,
        language,
        defaultPeerListId,
        activityHistoryFilters,
        selectedItem?.length ? (selectedItem[0] as TemplateExportListItemWithComments) : undefined,
        combinedSecurityIds
      );
    } else {
      if (reportType === ReportType.BnyManagementReport) {
        if (!sections.includes(ReportSection.BnyHeader)) {
          sections.push(ReportSection.BnyHeader);
        }
        return this.loadBnyReportData(
          sections,
          reportType,
          true,
          SAMPLE_DATA_SECURITY_ID,
          timeZone,
          culture,
          language,
          [],
          bnyReportTime
        );
      }

      return this.loadReportData(
        sections,
        includedContacts,
        reportType,
        '',
        true,
        securityId,
        dataSource,
        timeZone,
        currency,
        culture,
        language,
        defaultPeerListId,
        [],
        undefined,
        []
      );
    }
  }

  getExportData(
    reportSections: ReportSectionConfigurationWithOrder[],
    security: {
      id: number;
      surveillance: boolean;
    } | null,
    culture: string,
    language: string,
    currency: string | null,
    timeZone: string,
    defaultPeerListId: number,
    relatedSecurities?: Security[] | null,
    selectedInstitutionsOrContacts?: ListItem[],
    bnyReportTime: Date = new Date()
  ) {
    if (!reportSections.length) {
      return of({} as ReportDataMap);
    }
    const securityId = security?.id;
    const dataSource = security?.surveillance ? DataSource.Surveillance : DataSource.Public;
    const reportType = reportSections[0].config.appliedReportType;
    const combinedSecurityIds = relatedSecurities?.map(security => security?.id);

    const sections = Array.from(
      new Set(
        reportSections
          .filter(
            item =>
              item.config.reportSection !== ReportSection.None &&
              item.isSelected &&
              reportType &&
              item.config.applicableReportTypes.includes(reportType)
          )
          .map(section => section.config.reportSection)
      )
    );

    if (selectedInstitutionsOrContacts && selectedInstitutionsOrContacts.length) {
      const securityIds = selectedInstitutionsOrContacts.map(x => x.id);
      if (reportType === ReportType.BnyManagementReport) {
        return this.loadBnyExportData(
          sections,
          reportType,
          false,
          securityId,
          timeZone,
          culture,
          language,
          securityIds,
          bnyReportTime
        );
      }

      const profileIds = selectedInstitutionsOrContacts.map(
        institution => `${institution.prId ?? ''}|${institution.crmId ?? ''}`
      );

      const activityHistoryFilters: ActivityHistoryFilterRequest[] = selectedInstitutionsOrContacts
        .map(selectedItem => {
          const selectedListItem = selectedItem as TemplateExportListItemWithComments;
          return selectedListItem?.filterActivities
            ? {
                profileId: `${selectedItem.prId ?? ''}|${selectedItem.crmId ?? ''}`,
                companyContactIds: selectedListItem?.filterActivities?.companyContacts?.map(
                  contact => contact?.id as string
                ),
                externalInstitutionIds: selectedListItem?.filterActivities?.externalParticipants
                  ?.filter(item => item?.type == 'institutions')
                  .map(institution => institution?.id as string),
                externalContactIds: selectedListItem?.filterActivities?.externalParticipants
                  ?.filter(item => item?.type == 'contacts')
                  .map(institution => institution?.id as string)
              }
            : null;
        })
        .filter((filter): filter is ActivityHistoryFilterRequest => filter !== null);

      return this.loadExportData(
        sections,
        reportType,
        profileIds,
        false,
        securityId,
        dataSource,
        timeZone,
        currency,
        culture,
        language,
        defaultPeerListId,
        activityHistoryFilters,
        selectedInstitutionsOrContacts,
        combinedSecurityIds
      );
    } else {
      if (reportType === ReportType.BnyManagementReport) {
        return this.loadBnyExportData(
          sections,
          reportType,
          true,
          SAMPLE_DATA_SECURITY_ID,
          timeZone,
          culture,
          language,
          [],
          bnyReportTime
        );
      }

      return this.loadExportData(
        sections,
        reportType,
        [''],
        true,
        securityId,
        dataSource,
        timeZone,
        currency,
        culture,
        language,
        defaultPeerListId,
        [],
        selectedInstitutionsOrContacts,
        []
      );
    }
  }

  public getActivityFilters(selectedProfiles: TemplateExportListItemWithComments[]) {
    return selectedProfiles
      .map(p => {
        const filter = p.filterActivities;
        if (!filter) return null;

        const profileId = `${p.prId ?? ''}|${p.crmId ?? ''}`;
        const companyContactIds = filter.companyContacts?.map(c => c?.id as string);
        const externalInstitutionIds = filter.externalParticipants
          ?.filter(ep => ep?.type === 'institutions')
          .map(ep => ep?.id as string);
        const externalContactIds = filter.externalParticipants
          ?.filter(ep => ep?.type === 'contacts')
          .map(ep => ep?.id as string);

        return {
          profileId,
          companyContactIds,
          externalInstitutionIds,
          externalContactIds
        };
      })
      .filter(filter => !!filter);
  }

  public getComments(selectedProfiles: TemplateExportListItemWithComments[]) {
    return selectedProfiles
      .map(p => {
        const comment = p.comment ?? '';
        if (!comment) return null;
        const profileId = `${p.prId ?? ''}|${p.crmId ?? ''}`;

        return {
          comment,
          title: p.commentTitle ?? '',
          profileId: profileId
        };
      })
      .filter(comment => !!comment);
  }

  private loadBnyExportData(
    sectionsIn: ReportSection[],
    reportType: ReportType,
    pullSampleData = true,
    securityId: number | undefined,
    timeZone: string,
    culture: string,
    language: string,
    securityIds?: number[],
    bnyReportTime: Date = new Date()
  ) {
    const sections = sectionsIn;

    const bnyBody = {
      pullSampleData,
      securityId: securityId,
      securityIds: securityIds,
      reportTime: bnyReportTime,
      timeZone,
      language,
      culture,
      sections
    };

    if (securityId === SAMPLE_DATA_SECURITY_ID && this.cachedSampleExportData[reportType]) {
      return of(this.cachedSampleExportData[reportType]);
    }

    return this.http
      .post<ReportDataModel[]>(`${this.baseUrl}/configurable-reporting/bnyManagementReport/raw-data`, bnyBody)
      .pipe(
        map(reportDataList => {
          const reportData: ReportDataMap = reportDataList.reduce((acc, { key, value }) => {
            acc[key] = value;
            return acc;
          }, {} as ReportDataMap);
          return reportData;
        })
      );
  }

  private loadExportData(
    sectionsIn: ReportSection[],
    reportType: ReportType = ReportType.Institution,
    profileIdsIn: string[] = [''],
    pullSampleData = true,
    securityId: number = 115620,
    dataSource: string,
    timeZone: string,
    currency: string | null,
    culture: string,
    language: string,
    defaultPeerListId: number,
    activityHistoryFilters: ActivityHistoryFilterRequest[],
    selectedInstitutionsOrContacts?: ListItem[],
    combinedSecurityIds?: number[]
  ) {
    const profileIds = profileIdsIn.length ? profileIdsIn : [SAMPLE_DATA_PROFILE_ID];
    const sections = sectionsIn;
    const body = {
      profileIds,
      securityId,
      timeZone,
      reportType,
      dataSource,
      sections,
      currency,
      culture,
      language,
      sectionSettings: {
        EquityPortfolioTopBuysSellsGrid: '5',
        EquityPortfolioTopHoldingsGrid: '5'
      },
      pullSampleData,
      defaultPeerListId,
      activityHistoryFilters,
      combinedSecurityIds
    };

    if (profileIds.includes(SAMPLE_DATA_PROFILE_ID) && this.cachedSampleExportData[reportType]) {
      const dataSourceKey =
        dataSource?.trim()?.toLocaleLowerCase() === DataSource.Surveillance?.toLocaleLowerCase()
          ? DataSource.dataSource_Surveillance
          : DataSource.dataSource_Public;

      return of(
        this.cachedSampleExportData[reportType].map(cachedExportData => ({
          ...cachedExportData,
          reportData$: cachedExportData.reportData$.pipe(
            map(reportData => ({
              ...reportData,
              [ReportViewModelSection.Common_Footer]: {
                ...(reportData[ReportViewModelSection.Common_Footer] as CommonFooterModel),
                dataSource: { key: dataSourceKey }
              }
            }))
          )
        }))
      );
    }

    return this.http.post<ExportDataModel[]>(`${this.baseUrl}/configurable-reporting/profiles/raw-data`, body).pipe(
      map(reportDataList => {
        const dataSourceKey =
          dataSource?.trim()?.toLocaleLowerCase() === DataSource.Surveillance?.toLocaleLowerCase()
            ? DataSource.dataSource_Surveillance
            : DataSource.dataSource_Public;

        const exportData: ExportDataMap[] = reportDataList.map(({ profileId, reportViewModels }) => {
          const reportData: ReportDataMap = reportViewModels.reduce((acc, { key, value }) => {
            acc[key] = value;
            return acc;
          }, {} as ReportDataMap);
          const selectItem = selectedInstitutionsOrContacts?.find(
            institution => `${institution.prId ?? ''}|${institution.crmId ?? ''}` === profileId
          ) as TemplateExportListItemWithComments;
          if (selectItem.commentTitle || selectItem.comment) {
            reportData[ReportViewModelSection.Commentary] = {
              title: selectItem.commentTitle,
              comment: selectItem.comment
            };
          }

          const footerData = reportData[ReportViewModelSection.Common_Footer] as CommonFooterModel;
          if (footerData) {
            footerData.dataSource = { key: dataSourceKey };
          }

          return { profileId, reportData$: of(reportData) };
        });
        return exportData;
      }),
      tap(exportDataArray => {
        if (profileIds.includes(SAMPLE_DATA_PROFILE_ID)) {
          this.cachedSampleExportData[reportType] = exportDataArray;
        }
      })
    );
  }
  private loadBnyReportData(
    sectionsIn: ReportSection[],
    reportType: ReportType,
    pullSampleData = true,
    securityId: number | undefined,
    timeZone: string,
    culture: string,
    language: string,
    securityIds?: number[],
    bnyReportTime: Date = new Date()
  ) {
    const sections = sectionsIn;

    const bnyBody = {
      pullSampleData,
      securityId: securityId,
      securityIds: securityIds,
      reportTime: bnyReportTime,
      timeZone,
      language,
      culture,
      sections
    };

    if (securityId === SAMPLE_DATA_SECURITY_ID && this.cachedSampleReportData[reportType]) {
      return of(this.cachedSampleReportData[reportType]);
    }

    return this.http
      .post<ReportDataModel[]>(`${this.baseUrl}/configurable-reporting/bnyManagementReport/raw-data`, bnyBody)
      .pipe(
        map(reportDataList => {
          const reportData: ReportDataMap = reportDataList.reduce((acc, { key, value }) => {
            acc[key] = value;
            return acc;
          }, {} as ReportDataMap);
          return reportData;
        })
      );
  }

  private loadReportData(
    sectionsIn: ReportSection[],
    includedContacts: IncludedContacts[] = [],
    reportType: ReportType = ReportType.Institution,
    profileIdIn: string = '',
    pullSampleData = true,
    securityId: number = 115620,
    dataSource: string,
    timeZone: string,
    currency: string | null,
    culture: string,
    language: string,
    defaultPeerListId: number,
    activityHistoryFilters: ActivityHistoryFilterRequest[],
    selectedItem?: TemplateExportListItemWithComments,
    combinedSecurityIds?: number[]
  ) {
    const profileId = profileIdIn !== '' ? profileIdIn : SAMPLE_DATA_PROFILE_ID;
    const sections = sectionsIn;
    const reportedQuarters = selectedItem?.reportedQuarters || [];
    const body = {
      profileId,
      securityId,
      timeZone,
      reportType,
      dataSource,
      sections,
      currency,
      culture,
      language,
      sectionSettings: {
        EquityPortfolioTopBuysSellsGrid: '3',
        EquityPortfolioTopHoldingsGrid: '8'
      },
      pullSampleData,
      activityHistoryFilters,
      defaultPeerListId,
      combinedSecurityIds,
      includedContacts,
      reportedQuarters
    };
    if (profileId === SAMPLE_DATA_PROFILE_ID && this.cachedSampleReportData[reportType]) {
      // Use cached data but ensure the footer datasource reflects the current state
      const cachedData = this.cachedSampleReportData[reportType];
      const dataSourceKey =
        dataSource?.trim()?.toLocaleLowerCase() === DataSource.Surveillance?.toLocaleLowerCase()
          ? DataSource.dataSource_Surveillance
          : DataSource.dataSource_Public;

      return of({
        ...cachedData,
        [ReportViewModelSection.Common_Footer]: {
          ...(cachedData[ReportViewModelSection.Common_Footer] as CommonFooterModel),
          dataSource: { key: dataSourceKey }
        }
      });
    }

    return this.http.post<ReportDataModel[]>(`${this.baseUrl}/configurable-reporting/profile/raw-data`, body).pipe(
      map(reportDataList => {
        const reportData: ReportDataMap = reportDataList.reduce((acc, { key, value }) => {
          acc[key] = value;
          return acc;
        }, {} as ReportDataMap);
        if (!selectedItem) {
          reportData[ReportViewModelSection.Commentary] = {
            title: 'Comments',
            comment: Array(20).fill('This is placeholder data. ').join('')
          };
        } else if (selectedItem?.commentTitle || selectedItem?.comment) {
          reportData[ReportViewModelSection.Commentary] = {
            title: selectedItem.commentTitle,
            comment: selectedItem.comment
          };
        }

        if (reportData[ReportViewModelSection.Common_ParticipantBiography]) {
          reportData[ReportViewModelSection.InvestorProfile_PrimaryContactBiography] =
            reportData[ReportViewModelSection.Common_ParticipantBiography];
        }

        // check if activities data is present
        const footerData = reportData[ReportViewModelSection.Common_Footer] as CommonFooterModel;
        if (this.isActivityEmpty(reportData)) {
          footerData.calculatedEmptySectionLocalizationKeys = [
            ...(footerData.calculatedEmptySectionLocalizationKeys ?? []),
            'activities'
          ];
        }

        // check if ownership data is present
        if (this.isOwnershipEmpty(reportData)) {
          footerData.calculatedEmptySectionLocalizationKeys = [
            ...(footerData.calculatedEmptySectionLocalizationKeys ?? []),
            'ownership'
          ];
        }

        // Ensure the footer datasource reflects the current surveillance toggle state
        const dataSourceKey =
          dataSource?.trim()?.toLocaleLowerCase() === DataSource.Surveillance?.toLocaleLowerCase()
            ? DataSource.dataSource_Surveillance
            : DataSource.dataSource_Public;

        if (footerData) {
          footerData.dataSource = { key: dataSourceKey };
        }

        return reportData;
      }),
      tap(reportData => {
        if (profileId === SAMPLE_DATA_PROFILE_ID) {
          this.cachedSampleReportData[reportType] = reportData;
        }
      })
    );
  }

  private loadItineraryReportData(
    sectionsIn: ReportSection[],
    reportType: ReportType,
    eventId: string,
    activityIds: string[] = [],
    securityId: number | undefined,
    dataSource: string,
    timeZone: string,
    currency: string | null,
    culture: string,
    language: string,
    defaultPeerListId: number,
    activityHistoryFilters: ActivityHistoryFilterRequest[],
    templateExportListItemWithComments?: TemplateExportListItemWithComments,
    pullSampleData: boolean = false,
    selectedItineraryTab: string = 'events',
    templateId?: number | null
  ) {
    const sections = sectionsIn;
    const id = activityIds.length === 0 ? eventId || SAMPLE_DATA_EVENT_ID : null;
    const body = {
      eventId: id,
      activityIds: activityIds,
      securityId,
      timeZone,
      reportType,
      dataSource,
      sections,
      currency,
      culture,
      language,
      sectionSettings: {
        EquityPortfolioTopBuysSellsGrid: '',
        EquityPortfolioTopHoldingsGrid: ''
      },
      pullSampleData: id === SAMPLE_DATA_EVENT_ID && activityIds.length === 0,
      activityHistoryFilters,
      defaultPeerListId,
      fileName: reportType,
      maxActivityCount: ITINERARY_PREVIEW_ACTIVITY_LIMIT + 1,
      templateId: templateId
    };
    if (id === SAMPLE_DATA_EVENT_ID && activityIds.length === 0 && this.cachedSampleReportData[reportType]) {
      // Use cached data but ensure the footer datasource reflects the current state
      const cachedData = this.cachedSampleReportData[reportType];
      const dataSourceKey =
        dataSource?.trim()?.toLocaleLowerCase() === DataSource.Surveillance?.toLocaleLowerCase()
          ? DataSource.dataSource_Surveillance
          : DataSource.dataSource_Public;

      const updatedCachedData = {
        ...cachedData,
        [ReportViewModelSection.Common_Footer]: {
          ...(cachedData[ReportViewModelSection.Common_Footer] as CommonFooterModel),
          dataSource: { key: dataSourceKey }
        }
      };

      return of(this.transformItineraryReportData(updatedCachedData, selectedItineraryTab));
    }
    return this.http.post<ReportDataModel[]>(`${this.baseUrl}/configurable-reporting/itinerary/raw-data`, body).pipe(
      map(reportDataList => {
        const reportData: ReportDataMap = reportDataList.reduce((acc, { key, value }) => {
          acc[key] = value;
          return acc;
        }, {} as ReportDataMap);

        if (templateExportListItemWithComments?.commentTitle || templateExportListItemWithComments?.comment) {
          reportData[ReportViewModelSection.Commentary] = {
            title: templateExportListItemWithComments.commentTitle,
            comment: templateExportListItemWithComments.comment
          };
        }
        //Footer is not coming from itinerary raw data endpoint API,
        // so adding footer to to maintain the report generation with the investor/contact report
        const eventBaseInfo = reportData[ReportViewModelSection.Itinerary_EventBaseInfo] as EventBaseInfo | undefined;
        const existingFooter = reportData[ReportViewModelSection.Common_Footer] as CommonFooterModel | undefined;
        const dataSourceKey =
          dataSource?.trim()?.toLocaleLowerCase() === DataSource.Surveillance?.toLocaleLowerCase()
            ? DataSource.dataSource_Surveillance
            : DataSource.dataSource_Public;

        const commonFooter: CommonFooterModel = {
          ...existingFooter,
          dataSource: { key: dataSourceKey },
          name: existingFooter?.name ?? eventBaseInfo?.name,
          culture: existingFooter?.culture ?? eventBaseInfo?.culture ?? culture,
          emptySectionLocalizationKeys: existingFooter?.emptySectionLocalizationKeys ?? [],
          emptySectionNames: existingFooter?.emptySectionNames ?? []
        };

        reportData[ReportViewModelSection.Common_Footer] = commonFooter;

        return reportData;
      }),
      tap(reportData => {
        if (id === SAMPLE_DATA_EVENT_ID) {
          this.cachedSampleReportData[reportType] = reportData;
        }
      }),
      catchError((errorDetails: ReportExportError) => {
        return throwError(() => errorDetails);
      })
    );
  }

  private isActivityEmpty(reportData: Partial<Record<ReportViewModelSection, unknown>>): boolean {
    if (reportData.InvestorProfile_FinancialHighlights) {
      const activity = (reportData.InvestorProfile_FinancialHighlights as FinancialHighlightsModel)?.activity;
      if (
        activity == null ||
        (activity.lastActivityDate == null &&
          activity.primaryInstitutionActivitiesCount === 0 &&
          activity.primaryContactActivitiesCount === 0)
      ) {
        return true;
      }
    }
    return false;
  }

  private isOwnershipEmpty(reportData: Partial<Record<ReportViewModelSection, unknown>>): boolean {
    if (reportData.InvestorProfile_FinancialHighlights) {
      const ownership = (reportData.InvestorProfile_FinancialHighlights as FinancialHighlightsModel)?.ownership;
      if (ownership == null) {
        return true;
      }
    }
    return false;
  }

  private transformItineraryReportData(reportData: ReportDataMap, selectedItineraryTab: string): ReportDataMap {
    if (selectedItineraryTab === 'events') {
      return reportData;
    }

    const eventBaseInfo = reportData[ReportViewModelSection.Itinerary_EventBaseInfo] as EventBaseInfo | undefined;
    if (!eventBaseInfo) {
      return reportData;
    }

    // Preserve the footer data when transforming
    const transformedData = {
      ...reportData,
      [ReportViewModelSection.Itinerary_EventBaseInfo]: {
        ...eventBaseInfo,
        name: 'Itinerary Report'
      }
    };

    // Ensure footer is preserved if it exists
    if (reportData[ReportViewModelSection.Common_Footer]) {
      transformedData[ReportViewModelSection.Common_Footer] = reportData[ReportViewModelSection.Common_Footer];
    }

    return transformedData;
  }
}
